import { create } from 'zustand';
import { useEffect } from 'react';

/**
 * App-wide, opt-in "confirm before leaving" guard for `useViewTransitionNavigate`.
 *
 * `useViewTransitionNavigate` is the ONE function both the left rail (`AppShell`'s own
 * `handleNavClick`) and the topbar breadcrumb (`AppBreadcrumb`'s `onNavigate`) call for every
 * SPA-internal navigation — both are wired from `TenantLayout`'s single `useViewTransitionNavigate()`
 * instance. Consulting this store from inside that one hook therefore covers "sol ray" and
 * "breadcrumb" navigation with a single integration point, without a data router (`useBlocker`
 * needs `createBrowserRouter` — `App.tsx` mounts a plain `BrowserRouter`; see
 * `PrintTemplateDesignerPage`'s own doc comment for why this card did not migrate that).
 *
 * Deliberately NOT covered by this mechanism:
 *  - A page's own `useNavigate()` call (e.g. an in-page "Geri" button) — that page guards its own
 *    click handler directly; there is nothing to intercept centrally for a call this store never sees.
 *  - The browser's native back/forward button — no `popstate`-based interception is attempted here
 *    (see the same doc comment); only genuinely in-app navigation is guarded.
 *
 * At most one page registers a guard at a time (the currently-mounted route owning unsaved state).
 * The default (`null`) means "nothing to confirm" — every page that never calls
 * `useNavigationLeaveGuard` sees zero behavior change from this store's existence.
 */
interface NavigationGuardState {
  /** Returns true to ALLOW the navigation, false to cancel it. `null` = no guard active. */
  confirmLeave: (() => boolean) | null;
  setConfirmLeave: (fn: (() => boolean) | null) => void;
}

export const useNavigationGuardStore = create<NavigationGuardState>((set) => ({
  confirmLeave: null,
  setConfirmLeave: (confirmLeave) => set({ confirmLeave }),
}));

/**
 * Registers `confirmLeave` as the active navigation guard while `shouldBlock` is true, cleared again
 * on unmount or once `shouldBlock` flips back to false. `confirmMessage` is read fresh on every
 * navigation attempt (a `window.confirm` call), so a caller may safely pass a translated string that
 * changes between renders.
 *
 * The cleanup clears the slot only when IT is still the one holding it (`s.confirmLeave === fn`).
 * Today's single consumer never hits this, but the slot is shared app-wide: without the identity
 * check, an unmount from a stale effect run could clear a guard a DIFFERENT page has since
 * registered, silently un-guarding that page's own unsaved changes.
 *
 * `onConfirmedLeave` runs synchronously after the user confirms and BEFORE the navigation proceeds;
 * pass a stable reference, it is an effect dependency.
 */
export function useNavigationLeaveGuard(shouldBlock: boolean, confirmMessage: string, onConfirmedLeave?: () => void): void {
  const setConfirmLeave = useNavigationGuardStore((s) => s.setConfirmLeave);

  useEffect(() => {
    if (!shouldBlock) return;
    const fn = () => {
      const allowed = window.confirm(confirmMessage);
      if (allowed) onConfirmedLeave?.();
      return allowed;
    };
    setConfirmLeave(fn);
    return () => useNavigationGuardStore.setState((s) => (s.confirmLeave === fn ? { confirmLeave: null } : s));
  }, [shouldBlock, confirmMessage, setConfirmLeave, onConfirmedLeave]);
}
